import React, { useEffect, useState } from 'react';
import { getHealthData, triggerFitrockrSync } from '../../../../services/healthDataService';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { startOfWeek, endOfWeek, addWeeks, isWithinInterval } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { BarChart3, RefreshCw } from 'lucide-react';
import './HealthCharts.css';

const TIME_ZONE = 'Europe/Berlin';

const HealthCharts = ({ userId }) => {
  const [hrvRawData, setHrvRawData] = useState([]);
  const [hrvData, setHrvData] = useState([]);
  const [heartRateData, setHeartRateData] = useState([]);
  const [sleepRawData, setSleepRawData] = useState([]);
  const [sleepData, setSleepData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [rawHeartRateData, setRawHeartRateData] = useState([]);

  // Garmin-sourced metrics
  const [rawStressData, setRawStressData] = useState([]);
  const [stressData, setStressData] = useState([]);
  const [rawBodyBatteryData, setRawBodyBatteryData] = useState([]);
  const [bodyBatteryData, setBodyBatteryData] = useState([]);
  const [rawStepsData, setRawStepsData] = useState([]);
  const [stepsData, setStepsData] = useState([]);
  const [rawWearingTimeData, setRawWearingTimeData] = useState([]);
  const [wearingTimeData, setWearingTimeData] = useState([]);

  useEffect(() => {
    if (!userId) return;
    fetchData();
  }, [userId]);

  useEffect(() => {
    if (hrvRawData.length > 0) {
      setHrvData(filterHrvByWeek(hrvRawData, currentWeekStart));
    }
    if (rawHeartRateData.length > 0) {
      setHeartRateData(filterHeartRateByWeek(rawHeartRateData, currentWeekStart));
    }
    if (sleepRawData.length > 0) {
      setSleepData(filterSleepByWeek(sleepRawData, currentWeekStart));
    }
    if (rawStressData.length > 0) {
      setStressData(filterDailyByWeek(rawStressData, currentWeekStart));
    }
    if (rawBodyBatteryData.length > 0) {
      setBodyBatteryData(filterDailyByWeek(rawBodyBatteryData, currentWeekStart));
    }
    if (rawStepsData.length > 0) {
      setStepsData(filterDailyByWeek(rawStepsData, currentWeekStart));
    }
    if (rawWearingTimeData.length > 0) {
      setWearingTimeData(filterDailyByWeek(rawWearingTimeData, currentWeekStart));
    }
  }, [currentWeekStart, hrvRawData, rawHeartRateData, sleepRawData,
      rawStressData, rawBodyBatteryData, rawStepsData, rawWearingTimeData]);
  
  
  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await getHealthData(userId);
      const processedHrv = processHrvData(data.hrvData);
      const processedHeartRate = processHeartRateData(data.heartRateData);
      const processedSleep = processSleepData(data.sleepData);
      const processedStress = processDailyAverage(data.stressData);
      const processedBodyBattery = processDailyAverage(data.bodyBatteryData);
      const processedSteps = processDailyTotal(data.stepsData);
      const processedWearingTime = processWearingTime(data.wearingTimeData);

      setHrvRawData(processedHrv);
      setRawHeartRateData(processedHeartRate);
      setSleepRawData(processedSleep);
      setRawStressData(processedStress);
      setRawBodyBatteryData(processedBodyBattery);
      setRawStepsData(processedSteps);
      setRawWearingTimeData(processedWearingTime);

      setHeartRateData(filterHeartRateByWeek(processedHeartRate, currentWeekStart));
      setSleepData(filterSleepByWeek(processedSleep, currentWeekStart));
      setStressData(filterDailyByWeek(processedStress, currentWeekStart));
      setBodyBatteryData(filterDailyByWeek(processedBodyBattery, currentWeekStart));
      setStepsData(filterDailyByWeek(processedSteps, currentWeekStart));
      setWearingTimeData(filterDailyByWeek(processedWearingTime, currentWeekStart));
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Manual refresh: pull this user's latest data from Fitrockr, then re-read.
  const handleRefresh = async () => {
    setLoading(true);
    await triggerFitrockrSync(userId);
    await fetchData();
  };

  const processHrvData = (hrvArray) => {
    if (!hrvArray || hrvArray.length === 0) return [];

    const currentYear = new Date().getFullYear();
    const processed = [];

    hrvArray.forEach(entry => {
      const localDate = toZonedTime(entry.date, TIME_ZONE);
      if (localDate.getFullYear() !== currentYear) return;

      entry.values.forEach(({ timestamp, value }) => {
        const localTime = toZonedTime(timestamp, TIME_ZONE);
        processed.push({
          date: format(localTime, 'MMM dd'),
          timestamp: localTime,
          value: parseFloat(value.toFixed(2)),
        });
      });
    });

    return processed.sort((a, b) => a.timestamp - b.timestamp);
  };

  const filterHrvByWeek = (data, weekStart) => {
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
    return data
      .filter(d => isWithinInterval(d.timestamp, { start: weekStart, end: weekEnd }))
      .map(d => ({
        date: format(d.timestamp, 'MMM.dd HH:mm'),
        value: d.value,
      }));
  };

  const processHeartRateData = (hrArray) => {
    if (!hrArray || hrArray.length === 0) return [];
  
    const currentYear = new Date().getFullYear();
    const processed = [];
  
    hrArray.forEach(day => {
      const localDate = toZonedTime(day.date, TIME_ZONE);
      if (localDate.getFullYear() !== currentYear) return;
  
      day.values.forEach(({ timestamp, value }) => {
        const localTime = toZonedTime(timestamp, TIME_ZONE);
        processed.push({
          timestamp: localTime,
          date: format(localTime, 'MMM dd'),
          hourSlot: `${format(localTime, 'HH')}:00 - ${format(localTime, 'HH')}:59`,
          value,
        });
      });
    });
  
    return processed.sort((a, b) => a.timestamp - b.timestamp);
  };

  const filterHeartRateByWeek = (data, weekStart) => {
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
    const grouped = {};
  
    const filtered = data.filter(entry =>
      isWithinInterval(entry.timestamp, { start: weekStart, end: weekEnd })
    );
  
    filtered.forEach(entry => {
      const { date, hourSlot, value } = entry;
      if (!grouped[date]) grouped[date] = {};
      if (!grouped[date][hourSlot]) grouped[date][hourSlot] = [];
      grouped[date][hourSlot].push(value);
    });
  
    const result = [];
    Object.entries(grouped).forEach(([date, hourData]) => {
      Object.entries(hourData).forEach(([hourSlot, values]) => {
        result.push({
          date,
          hourSlot,
          min: Math.round(Math.min(...values)),
          max: Math.round(Math.max(...values)),
        });
      });
    });
  
    return result;
  };

  const processSleepData = (sleepArray) => {
    if (!sleepArray || sleepArray.length === 0) return [];
  
    return sleepArray.map(entry => {
      const start = toZonedTime(entry.sessionStart, TIME_ZONE);
      const end = toZonedTime(entry.sessionEnd, TIME_ZONE);
      const day = format(toZonedTime(entry.date, TIME_ZONE), 'EEE');
  
      return {
        timestamp: new Date(entry.date),
        day,
        duration: entry.totalDuration,
        startTime: format(start, 'HH:mm'),
        endTime: format(end, 'HH:mm'),
        // Garmin-only enrichments (undefined for Apple-sourced sleep)
        sleepScore: entry.sleepScore,
        deepSleep: entry.deepSleep,
        lightSleep: entry.lightSleep,
        remSleep: entry.remSleep,
        awake: entry.awake,
      };
    });
  };
  const weekdayOrder = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // --- Garmin metric processors ---

  // Daily average of a date-grouped {values:[{timestamp,value}]} metric (stress, body battery).
  const processDailyAverage = (arr) => {
    if (!arr || arr.length === 0) return [];
    const currentYear = new Date().getFullYear();
    const result = [];
    arr.forEach((entry) => {
      const localDate = toZonedTime(entry.date, TIME_ZONE);
      if (localDate.getFullYear() !== currentYear) return;
      if (!entry.values || entry.values.length === 0) return;
      const avg = entry.values.reduce((sum, v) => sum + v.value, 0) / entry.values.length;
      result.push({ timestamp: localDate, value: Math.round(avg) });
    });
    return result.sort((a, b) => a.timestamp - b.timestamp);
  };

  // Daily total of a date-grouped metric (steps): sum the day's samples.
  const processDailyTotal = (arr) => {
    if (!arr || arr.length === 0) return [];
    const currentYear = new Date().getFullYear();
    const result = [];
    arr.forEach((entry) => {
      const localDate = toZonedTime(entry.date, TIME_ZONE);
      if (localDate.getFullYear() !== currentYear) return;
      if (!entry.values || entry.values.length === 0) return;
      const total = entry.values.reduce((sum, v) => sum + v.value, 0);
      result.push({ timestamp: localDate, value: Math.round(total) });
    });
    return result.sort((a, b) => a.timestamp - b.timestamp);
  };

  // Wearing time: sum per-interval minutes into hours worn per day.
  const processWearingTime = (arr) => {
    if (!arr || arr.length === 0) return [];
    const currentYear = new Date().getFullYear();
    const result = [];
    arr.forEach((entry) => {
      const localDate = toZonedTime(entry.date, TIME_ZONE);
      if (localDate.getFullYear() !== currentYear) return;
      if (!entry.values || entry.values.length === 0) return;
      const minutes = entry.values.reduce((sum, v) => sum + v.value, 0);
      result.push({ timestamp: localDate, value: parseFloat((minutes / 60).toFixed(1)) });
    });
    return result.sort((a, b) => a.timestamp - b.timestamp);
  };

  // Generic weekly filter for daily-resolution series → [{ date: 'EEE dd', value }].
  const filterDailyByWeek = (data, weekStart) => {
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
    return data
      .filter((d) => isWithinInterval(d.timestamp, { start: weekStart, end: weekEnd }))
      .sort((a, b) => a.timestamp - b.timestamp)
      .map((d) => ({ date: format(d.timestamp, 'EEE dd'), value: d.value }));
  };

  const filterSleepByWeek = (data, weekStart) => {
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
  
    const filtered = data.filter(entry =>
      isWithinInterval(entry.timestamp, { start: weekStart, end: weekEnd })
    );
  
    return filtered.sort((a, b) => {
      return (
        weekdayOrder.indexOf(a.day) - weekdayOrder.indexOf(b.day)
      );
    });
  };
  
  const formatDuration = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  };
  

  const renderNoData = () => (
    <div className="no-data-message">
      <BarChart3 className="no-data-icon" size={32} strokeWidth={1.5} />
      <p>No data available for this time range.</p>
    </div>
  );
  
  const handlePreviousWeek = () => {
    console.log('Previous week clicked');
    setCurrentWeekStart(prev => {
      const newDate = addWeeks(prev, -1);
      console.log('New week start:', newDate);
      return newDate;
    });
  };

  const handleCurrentWeek = () => {
    console.log('Current week clicked');
    const newDate = startOfWeek(new Date(), { weekStartsOn: 1 });
    console.log('Setting to current week:', newDate);
    setCurrentWeekStart(newDate);
  };

  const handleNextWeek = () => {
    console.log('Next week clicked');
    setCurrentWeekStart(prev => {
      const newDate = addWeeks(prev, 1);
      console.log('New week start:', newDate);
      return newDate;
    });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="tooltip-value" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="health-charts-wrapper" style={{ position: 'relative', zIndex: 1 }}>
      <div className="health-charts-container" style={{ position: 'relative', zIndex: 2 }}>
        <div className="chart-navigation" style={{ position: 'relative', zIndex: 1000 }}>
          <div className="week-navigation" style={{ position: 'relative', zIndex: 1001 }}>
            <button
              className="nav-btn previous-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('BUTTON CLICKED - Previous Week');
                handlePreviousWeek();
              }}
              type="button"
              style={{
                pointerEvents: 'auto',
                cursor: 'pointer',
                position: 'relative',
                zIndex: 1002,
                backgroundColor: '#1f6da2'
              }}
            >
              <span className="nav-icon">‹</span> Previous Week
            </button>

            <button
              className="nav-btn current-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('BUTTON CLICKED - Current Week');
                handleCurrentWeek();
              }}
              type="button"
              style={{
                pointerEvents: 'auto',
                cursor: 'pointer',
                position: 'relative',
                zIndex: 1002,
                backgroundColor: '#1d455c'
              }}
            >
              Current Week
            </button>

            <button
              className="nav-btn next-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('BUTTON CLICKED - Next Week');
                handleNextWeek();
              }}
              type="button"
              style={{
                pointerEvents: 'auto',
                cursor: 'pointer',
                position: 'relative',
                zIndex: 1002,
                backgroundColor: '#1f6da2'
              }}
            >
              Next Week <span className="nav-icon">›</span>
            </button>
          </div>
          <p className="week-range">
            Week of {format(currentWeekStart, 'MMM dd')} – {format(endOfWeek(currentWeekStart, { weekStartsOn: 1 }), 'MMM dd, yyyy')}
          </p>
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <button
              type="button"
              className="refresh-btn"
              onClick={handleRefresh}
              disabled={loading}
              title="Pull latest from Garmin and refresh"
            >
              <RefreshCw size={16} className={loading ? 'refresh-spin' : ''} />
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        <div className="chart-section">
          <div className="chart-header">
            <h3>Heart Rate Variability (HRV)</h3>
          </div>
          <div className="chart-container">
            {loading ? (
              <div className="loading-state">Loading HRV data...</div>
            ) : hrvData.length === 0 ? (
              <div className="no-data-message">
                <BarChart3 className="no-data-icon" size={32} strokeWidth={1.5} />
                <p>No data available for this time range.</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={hrvData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    stroke="#e5e7eb"
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    stroke="#e5e7eb"
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                    iconType="line"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#2A9D8F" 
                    strokeWidth={2}
                    dot={{ fill: '#2A9D8F', r: 4 }}
                    activeDot={{ r: 6 }}
                    name="HRV Value"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="chart-section">
          <div className="chart-header">
            <h3>Heart Rate (Hourly Min/Max)</h3>
          </div>
          <div className="chart-container">
            {loading ? (
              <div className="loading-state">Loading heart rate data...</div>
            ) : heartRateData.length === 0 ? (
              <div className="no-data-message">
                <BarChart3 className="no-data-icon" size={32} strokeWidth={1.5} />
                <p>No data available for this time range.</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={heartRateData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                  <XAxis
                    dataKey={(entry) => `${entry.date} ${entry.hourSlot}`}
                    tick={{ fontSize: 10, fill: '#6b7280', angle: -45, textAnchor: 'end' }}
                    stroke="#e5e7eb"
                    height={100}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    stroke="#e5e7eb"
                    label={{ value: 'BPM', angle: -90, position: 'insideLeft' }}
                    domain={['dataMin - 5', 'dataMax + 5']}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="custom-tooltip">
                            <p className="tooltip-label">{label}</p>
                            <p className="tooltip-value" style={{ color: '#C85C6E' }}>
                              Max HR: {payload.find(p => p.dataKey === 'max')?.value} bpm
                            </p>
                            <p className="tooltip-value" style={{ color: '#2A9D8F' }}>
                              Min HR: {payload.find(p => p.dataKey === 'min')?.value} bpm
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="max"
                    stroke="#C85C6E"
                    strokeWidth={2}
                    dot={{ fill: '#C85C6E', r: 4 }}
                    activeDot={{ r: 6 }}
                    name="Maximum HR"
                  />
                  <Line
                    type="monotone"
                    dataKey="min"
                    stroke="#2A9D8F"
                    strokeWidth={2}
                    dot={{ fill: '#2A9D8F', r: 4 }}
                    activeDot={{ r: 6 }}
                    name="Minimum HR"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="chart-section">
          <div className="chart-header">
            <h3>Sleep Duration</h3>
          </div>
          <div className="chart-container">
            {loading ? (
              <div className="loading-state">Loading sleep data...</div>
            ) : sleepData.length === 0 ? (
              <div className="no-data-message">
                <BarChart3 className="no-data-icon" size={32} strokeWidth={1.5} />
                <p>No data available for this time range.</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={sleepData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <XAxis 
                    dataKey="day"
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    stroke="#e5e7eb"
                  />
                  <YAxis 
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    stroke="#e5e7eb"
                    label={{ value: 'Minutes', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="custom-tooltip">
                            <p className="tooltip-label">{label}</p>
                            <p className="tooltip-value">
                              Duration: {formatDuration(payload[0].value)}
                            </p>
                            <p className="tooltip-value">
                              Sleep: {payload[0].payload.startTime} – {payload[0].payload.endTime}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend 
                    wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                  />
                  <Bar
                    dataKey="duration"
                    fill="#5B6B9E"
                    name="Total Sleep"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Sleep Score (Garmin) */}
        <div className="chart-section">
          <div className="chart-header">
            <h3>Sleep Score</h3>
          </div>
          <div className="chart-container">
            {loading ? (
              <div className="loading-state">Loading sleep score...</div>
            ) : !sleepData.some((d) => d.sleepScore != null) ? (
              renderNoData()
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={sleepData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#6b7280' }} stroke="#e5e7eb" />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: '#6b7280' }} stroke="#e5e7eb" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} iconType="line" />
                  <Line
                    type="monotone"
                    dataKey="sleepScore"
                    stroke="#5B6B9E"
                    strokeWidth={2}
                    dot={{ fill: '#5B6B9E', r: 4 }}
                    activeDot={{ r: 6 }}
                    name="Sleep Score"
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Sleep Stages (Garmin) */}
        <div className="chart-section">
          <div className="chart-header">
            <h3>Sleep Stages</h3>
          </div>
          <div className="chart-container">
            {loading ? (
              <div className="loading-state">Loading sleep stages...</div>
            ) : !sleepData.some((d) => d.deepSleep != null || d.lightSleep != null || d.remSleep != null) ? (
              renderNoData()
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={sleepData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="day" tick={{ fontSize: 12, fill: '#6b7280' }} stroke="#e5e7eb" />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    stroke="#e5e7eb"
                    label={{ value: 'Minutes', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                  <Bar dataKey="deepSleep" stackId="sleep" fill="#3B4A6B" name="Deep" />
                  <Bar dataKey="lightSleep" stackId="sleep" fill="#5B6B9E" name="Light" />
                  <Bar dataKey="remSleep" stackId="sleep" fill="#8290B8" name="REM" />
                  <Bar dataKey="awake" stackId="sleep" fill="#E0954A" name="Awake" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Stress Level (Garmin) */}
        <div className="chart-section">
          <div className="chart-header">
            <h3>Stress Level (Daily Average)</h3>
          </div>
          <div className="chart-container">
            {loading ? (
              <div className="loading-state">Loading stress data...</div>
            ) : stressData.length === 0 ? (
              renderNoData()
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={stressData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6b7280' }} stroke="#e5e7eb" />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: '#6b7280' }} stroke="#e5e7eb" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} iconType="line" />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#D08A3E"
                    strokeWidth={2}
                    dot={{ fill: '#D08A3E', r: 4 }}
                    activeDot={{ r: 6 }}
                    name="Avg Stress"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Body Battery (Garmin) */}
        <div className="chart-section">
          <div className="chart-header">
            <h3>Body Battery (Daily Average)</h3>
          </div>
          <div className="chart-container">
            {loading ? (
              <div className="loading-state">Loading Body Battery...</div>
            ) : bodyBatteryData.length === 0 ? (
              renderNoData()
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={bodyBatteryData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6b7280' }} stroke="#e5e7eb" />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: '#6b7280' }} stroke="#e5e7eb" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} iconType="line" />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#2A9D8F"
                    strokeWidth={2}
                    dot={{ fill: '#2A9D8F', r: 4 }}
                    activeDot={{ r: 6 }}
                    name="Avg Body Battery"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Steps */}
        <div className="chart-section">
          <div className="chart-header">
            <h3>Steps (Daily Total)</h3>
          </div>
          <div className="chart-container">
            {loading ? (
              <div className="loading-state">Loading steps...</div>
            ) : stepsData.length === 0 ? (
              renderNoData()
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stepsData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6b7280' }} stroke="#e5e7eb" />
                  <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} stroke="#e5e7eb" />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                  <Bar dataKey="value" fill="#E0954A" name="Steps" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Wearing Time (Garmin compliance) */}
        <div className="chart-section">
          <div className="chart-header">
            <h3>Device Wearing Time</h3>
          </div>
          <div className="chart-container">
            {loading ? (
              <div className="loading-state">Loading wearing time...</div>
            ) : wearingTimeData.length === 0 ? (
              renderNoData()
            ) : (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={wearingTimeData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6b7280' }} stroke="#e5e7eb" />
                  <YAxis
                    domain={[0, 24]}
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                    stroke="#e5e7eb"
                    label={{ value: 'Hours', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }} />
                  <Bar dataKey="value" fill="#647B8C" name="Hours Worn" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthCharts;